import { Inject } from '@odinjs/odin';

import { Endpoint, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { ActionAPI, DeckAPI, FetchAPI, FieldAPI, FormAPI, PanelAPI } from '@philips-emr/tws-components';
import Controller, { OnAfterFetch, OnCancel, OnClick, OnClose, OnMount, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { NegotiationAttachmentsAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { NegotiationStatus } from './negotiation-controller';

interface NegotiationAttachment {
  ieStatus: number,
}

@Controller({ fragment: FragmentNames.NEGOTIATION_ATTACHMENTS })
class NegotiationAttachmentsController extends BaseController {

  @Inject(Aliases.PANEL_ATTACHMENT_LIST)
  private panelAttachmentsList: PanelAPI;
  @Inject(Aliases.FETCH_ATTACHMENTS)
  private fetchAttachments: FetchAPI;
  @Inject(Aliases.FORM_SEND_ATTACHMENTS)
  private formAttachment: FormAPI;
  @Inject(Aliases.FIELD_FILE_UPLOADER)
  private fieldUpload: FieldAPI;
  @Inject(Aliases.PANEL_ADD_ATTACHMENTS)
  private panelAddAttachments: PanelAPI;
  @Inject(Aliases.ACTION_ADD_NEW_ATTACHMENTS)
  private actionAddNewAttachments: ActionAPI;

  @OnMount(Aliases.PANEL_ATTACHMENTS)
  onMountPanelAttchaments(): void {
    const { nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    HttpService.get<NegotiationAttachment>(Endpoint.GET.NEGOTIATION_DETAIL, { nrSeqNegotiation })
      .then(({ body }) => {
        if (body) {
          const { ieStatus } = body;
          this.actionAddNewAttachments.setEnabled(ieStatus === NegotiationStatus.NEGOTIATING);
        }
      }).catch(() => {
        ToastServiceProviderAccreditation.error(MessageService.translate('pa.error-process-request'));
      });
  }

  @OnMount(Aliases.FETCH_ATTACHMENTS)
  onMountFetchAttchaments(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqNegotiation });
    api.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_ATTACHMENTS)
  onAfterFetchAttachments(event: FetchEvent): void {
    this.panelAttachmentsList.setVisible(!isEmptyArrayOrObject(event.data));
  }

  @OnSubmit(Aliases.FORM_SEND_ATTACHMENTS)
  onSubmitReject(event: SubmitEvent): void {
    const { nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    const body = event.record;
    void HttpService.post(Endpoint.POST.INSERT_ATTACHMENT_NEGOTIATION, { nrSeqNegotiation }, { body }).then(() => {
      this.panelAttachmentsList.setVisible(true);
      this.formAttachment.setData({});
      this.formAttachment.setRecord({});
      this.fieldUpload.setState({ showValidations: false });
      this.fetchAttachments.forceFetch();
      this.panelAddAttachments.setVisible(false);
    });
  }

  @OnSelectItem(Aliases.DECK_ATTACHMENTS)
  onSelectAttachment(event: SelectItemEvent<any>): void {
    const api = event.target as DeckAPI;
    const { nrSeqAttachment, fileName } = event.item;
    const { nrSeqNegotiation } = this.historyService.getCurrentRouteParams();

    FileService.download(Endpoint.GET.NEGOTIATION_ATTACHMENT, fileName, { nrSeqNegotiation, nrSeqAttachment }, HttpMethod.GET).then(() => {
      ToastServiceProviderAccreditation.informationBubble(MessageService.translate('pa.download-initiated'));
      api.unselectAll();
    })
      .catch(() => {
        ToastServiceProviderAccreditation.error(MessageService.translate('pa.error-finding-file'));
        api.unselectAll();
      });
  }

  @OnCancel(Aliases.FORM_SEND_ATTACHMENTS)
  onCancelFormSendAttachments(): void {
    this.panelAddAttachments.setVisible(false);
  }

  @OnClick(Aliases.ACTION_ADD_NEW_ATTACHMENTS)
  onClickActionAddNewAttachments(): void {
    this.panelAddAttachments.setVisible(true);
  }

  @OnClose(Aliases.MODAL_ATTACHMENTS)
  onCloseModalAttachments(): void {
    this.panelAddAttachments.setVisible(false);
  }

}

export {
  NegotiationAttachmentsController,
};
